<template>
  <div id="app" v-loading="loading">
    <router-view />
    <!-- Pad竖屏强制横屏提示，仅学生页面展示 -->
    <div
        class="orientation-box"
        @touchmove.prevent
        v-if="$store.getters.isPad && isStudentPage"
    >
      <div class="orientation-img"></div>
    </div>
  </div>
</template>

<script>
import { getProfile, getStudying } from "@/api/user";
import {logout, refresh} from "@/api/security";
import { setUserInfo, setToken, removeToken, removeAll } from "@/utils/auth";
import { isPC, isPad } from "@/utils/is-rules";

// ================== 常量统一管理 ==================
const LANG = {
  TC: "zh_tc",
  EN: "en"
};
const STUDY_STATUS = {
  NOT_STUDY: 0,
  STUDYING: 1
};
// 需要强制横屏的学生页面路由白名单
const STUDENT_PAGE_ROUTES = [
  "/libraryMode",
  "/testMode",
  "/adventure",
  "/studentAccount",
  "/assessment",
  "/examination",
  "/answerResult",
  "/help",
  "/selectSubscribed",
  "/gacha",
  "/myGacha",
  "/examinationAnswerResult"
];

export default {
  name: "App",
  data() {
    return {
      leaveSwitch: false,
      loading: false,
      beforeUnload_time: 0,
      gap_time: 0
    };
  },
  created() {
    this.tee();
  },
  mounted() {
    // 保存处理函数引用，销毁时正常移除监听（修复内存泄漏）
    this.beforeunloadHandlerBind = this.beforeunloadHandler.bind(this);
    this.unloadHandlerBind = this.unloadHandler.bind(this);
    window.addEventListener("beforeunload", this.beforeunloadHandlerBind);
    window.addEventListener("unload", this.unloadHandlerBind);

    // 首次进入初始化用户信息
    if (this.$store.getters.isFirst) {
      this.$store.dispatch("setIsFirst", false);
      const { roleName } = this.userInfo || {};
      if (roleName === this.roleNameParent || roleName === this.roleNameStudent) {
        this.getProfile();
      }
    }
  },
  destroyed() {
    // 移除监听，必须使用同一个函数引用
    window.removeEventListener("beforeunload", this.beforeunloadHandlerBind);
    window.removeEventListener("unload", this.unloadHandlerBind);
  },
  computed: {
    // 当前登录用户信息
    userInfo() {
      return this.$store.getters.userProfile || {};
    },
    // 家长角色标识
    roleNameParent() {
      return "parent";
    },
    // 学生角色标识
    roleNameStudent() {
      return "student";
    },
    // 刷新token
    refreshToken() {
      return this.userInfo.refreshToken || "";
    },
    // 判断是否为学生专属页面
    isStudentPage() {
      return STUDENT_PAGE_ROUTES.includes(this.$route.path);
    }
  },
  methods: {
    /** 统一切换全局语言 */
    setGlobalLang(lang) {
      this.$i18n.locale = lang;
      this.$store.dispatch("setLanguage", lang);
      localStorage.setItem("app_lang", lang);
    },
    /** 初始化设备类型存入vuex */
    tee() {
      this.$store.dispatch("setIsPC", isPC());
      this.$store.dispatch("setIsPad", isPad());
    },

    /** 刷新token + 获取用户信息 */
    async getProfile() {
      this.loading = false;
      try {
        // 1. 等待刷新token完成
        const reqData = { refreshToken: this.refreshToken };
        const v = await refresh(reqData);
        setToken(v.data.accessToken);

        // 2. refresh 结束后请求就读状态
        const studying = await getStudying();

        // 3. 获取用户资料（这里不会递归，只是调用接口函数，和当前方法重名而已）
        const profileRes = await getProfile();
        const profileData = profileRes.data;

        // 家长角色
        if (this.userInfo.roleName === this.roleNameParent) {
          setUserInfo("userProfile", profileData);
          this.setGlobalLang(LANG.TC);
        }
        // 学生角色
        else if (this.userInfo.roleName === this.roleNameStudent) {
          const detail = JSON.parse(JSON.stringify(profileData));
          const subIndex = 0;
          detail.subscriptionIndex = subIndex;
          setUserInfo("userProfile", detail);

          const targetSub = detail.subscriptions[subIndex];
          this.$store.dispatch("setGradeId", targetSub.gradeId);

          if (studying.data === STUDY_STATUS.STUDYING) {
            this.setGlobalLang(LANG.EN);
          } else if (studying.data === STUDY_STATUS.NOT_STUDY) {
            this.setGlobalLang(LANG.TC);
          }
        }
      } catch (err) {
        console.error("Refresh token / get profile error：", err);
        // this.logout1()
      } finally {
        // 所有串行接口全部执行完毕后，才关闭loading
        this.loading = false;
      }
    },

    /** 页面关闭前记录时间戳 */
    beforeunloadHandler() {
      this.beforeUnload_time = new Date().getTime();
    },

    /** 判断是刷新页面还是关闭窗口 */
    unloadHandler() {
      this.gap_time = new Date().getTime() - this.beforeUnload_time;
      // 间隔≤5ms 判定为关闭标签页/浏览器，>5ms为页面刷新
      if (this.gap_time <= 5) {
        // 关闭页面清除缓存（按需开启）
        // removeAll();
        // removeToken();
      }
    },

    /** 登出逻辑 */
    logout1() {
      const form = { refreshToken: this.refreshToken };
      logout(form)
          .then(() => this.clearLoginData())
          .catch(() => this.clearLoginData());
    },

    /** 抽离登出清除缓存公共逻辑 */
    clearLoginData() {
      removeToken();
      removeAll();
      window.sessionStorage.clear();
      this.leaveSwitch = false;
      this.$router.push({ path: "/login" });
    }
  }
};
</script>

<style lang="scss">
html,
body,
#app {
  margin: 0px;
  height: 100% !important;
}

.el-loading-spinner {
  font-size: 60px;
}

// Pad竖屏强制横屏遮罩
@media (orientation: portrait) {
  .orientation-box {
    z-index: 99999;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);

    &::after {
      content: "為了更好的體驗，請將iPad轉過來橫屏顯示";
      text-align: center;
      font-size: 16px;
      color: #fff;
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      margin-top: 50px;
      z-index: 99999;
    }

    .orientation-img {
      position: absolute;
      z-index: 99999;
      height: 200px;
      width: 100px;
      left: 50%;
      top: 50%;
      margin: -140px 0 0 -50px;
      background-image: url("data:image/png;base64,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");
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 100px auto;
      transform: rotateZ(-90deg);
      -webkit-transform: rotateZ(-90deg);
    }
  }
}
</style>